// CommercialFXPage.js - Dólar Comercial & Macroeconomia
import React, { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { NewsCardsSection } from '../components/NewsCards_Real';
import { useCurrencies } from '../components/useMarketData';

// ============================================
// REAL DATA SIMULATION (MACRO MOCKS)
// ============================================
const FETCH_FX_DATA = async () => {
  return [
    { pair: 'Dólar Futuro (DOL1!)', name: 'Contrato Futuro B3', rate: 5.48, change: +0.45, region: 'BRICS', type: 'Fi', lore: 'A verdadeira referência do mercado brasileiro corporativo.' },
    { pair: 'EUR/USD', name: 'The Fiber', rate: 1.08, change: +0.22, region: 'G7', type: 'Fi', lore: 'Most traded pair globally.' },
    { pair: 'NZD/USD', name: 'The Kiwi', rate: 0.61, change: +0.85, region: 'Oceania', type: 'Ag', lore: 'Bio-tech backed exports.' },
    { pair: 'USD/JPY', name: 'The Gopher', rate: 151.2, change: -0.40, region: 'G7', type: 'Safe', lore: 'Carry trade funding currency.' },
  ];
};

const FETCH_COMMERCE_DATA = async () => {
  return [
    { month: 'Set', export: 28.5, import: 21.2, balance: 7.3 },
    { month: 'Out', export: 30.1, import: 22.8, balance: 7.3 },
    { month: 'Nov', export: 29.8, import: 23.5, balance: 6.3 },
    { month: 'Dez', export: 32.2, import: 24.1, balance: 8.1 }, 
    { month: 'Jan', export: 31.5, import: 23.9, balance: 7.6 },
  ];
};

// ============================================
// COMPONENTS
// ============================================
const CurrencyClusters = ({ theme, data }) => {
  const clusters = {
    'G7 / Otan': data.filter(d => d.region === 'G7'),
    'BRICS / Commodities / Futuros': data.filter(d => d.region === 'BRICS' || d.region === 'Oceania'),
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
      {Object.entries(clusters).map(([key, items]) => (
        <div key={key} style={{ background: theme.cardBg, borderRadius: '16px', padding: '1.5rem', border: `1px solid ${theme.border}` }}>
          <h3 style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px', color: theme.textSecondary, marginBottom: '1rem', borderBottom: `1px solid ${theme.border}`, paddingBottom: '0.5rem' }}>
            {key}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{item.pair}</div>
                  <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>{item.name}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>{item.rate}</div>
                  <div style={{ fontSize: '0.8rem', color: item.change >= 0 ? '#4ade80' : '#ef4444' }}>
                    {item.change > 0 ? '+' : ''}{item.change}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const LoreCard = ({ theme, pair, lore, title }) => (
  <div style={{
    background: `linear-gradient(135deg, ${theme.cardBg}, rgba(0,0,0,0))`, border: `1px solid ${theme.border}`,
    borderRadius: '12px', padding: '1.5rem', position: 'relative', overflow: 'hidden'
  }}>
    <div style={{ position: 'relative', zIndex: 2 }}>
      <div style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', background: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', fontSize: '0.7rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
        {pair} • MACRO DEEP DIVE
      </div>
      <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{title}</h3>
      <p style={{ fontSize: '0.9rem', color: theme.textSecondary, lineHeight: '1.5' }}>{lore}</p>
    </div>
  </div>
);

const MacroChart = ({ theme, data }) => (
  <div style={{ background: theme.cardBg, borderRadius: '16px', padding: '1.5rem', border: `1px solid ${theme.border}` }}>
    <h3 style={{ fontSize: '1.3rem', fontWeight: 'bold', color: theme.textPrimary, marginBottom: '0.5rem' }}>
      Balança Comercial (Brasil)
    </h3>
    <p style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '1.5rem' }}>
      Exportações superando importações = Entrada líquida de dólares (Pressão de baixa no USD/BRL).
    </p>

    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
        <XAxis dataKey="month" stroke={theme.textSecondary} tick={{fontSize: 12}} />
        <YAxis stroke={theme.textSecondary} tick={{fontSize: 12}} />
        <Tooltip contentStyle={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: '8px' }} formatter={(value) => [`US$ ${value} Bi`, '']} />
        <Bar dataKey="export" stackId="a" fill="#4ade80" radius={[4, 4, 0, 0]} />
        <Bar dataKey="import" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

// ============================================
// MAIN PAGE
// ============================================
export default function CommercialFXPage() {
  const [isDark, setIsDark] = useState(true);
  const [fxData, setFxData] = useState([]);
  const [commerceData, setCommerceData] = useState([]);
  
  const { data: apiCurrencies } = useCurrencies();

  // TICKER TAPE FORMATTING - THIS WAS MISSING
  const getFxTickers = useMemo(() => {
    if (!apiCurrencies || !Array.isArray(apiCurrencies)) return [];
    return apiCurrencies
      .filter(item => typeof item.last_quote === 'number' && item.last_quote !== 0) 
      .map(item => ({
        ticker: item.symbol,       
        name: item.name,           
        ult: 1 / item.last_quote,      
        var: item.rate * -1             
      }));
  }, [apiCurrencies]);

  const theme = {
    background: isDark ? 'rgba(5, 5, 5, 0.95)' : 'rgba(245, 245, 245, 0.95)',
    textPrimary: isDark ? '#fff' : '#111',
    textSecondary: isDark ? '#a1a1aa' : '#666',
    cardBg: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(255, 255, 255, 0.8)',
    border: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.1)',
  };

  useEffect(() => {
    const loadData = async () => {
      setFxData(await FETCH_FX_DATA());
      setCommerceData(await FETCH_COMMERCE_DATA());
    };
    loadData();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: isDark ? '#000' : '#f0f0f0', color: theme.textPrimary, position: 'relative' }}>
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
        <Silk speed={15} scale={4} color={isDark ? "#1e293b" : "#cbd5e1"} noiseIntensity={isDark ? 5 : 2} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header 
          theme={theme} 
          isDark={isDark} 
          toggleTheme={() => setIsDark(!isDark)} 
          currency="USD"
          topTickers={getFxTickers} // FIXED HERE
        />

        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
          
          <div style={{ marginBottom: '4rem', marginTop: '2rem' }}>
            <h1 style={{ 
              fontSize: '3.5rem', fontWeight: '900', letterSpacing: '-2px', marginBottom: '0.5rem',
              background: 'linear-gradient(to right, #4ade80, #16a34a)', 
              WebkitBackgroundClip: 'text', color: 'transparent'
            }}>
              Mercado Cambial & Macro
            </h1>
            <p style={{ fontSize: '1.2rem', color: theme.textSecondary, maxWidth: '600px' }}>
              Fluxo comercial, mercado futuro (B3) e geopolítica impactando o Dólar Comercial.
            </p>
          </div>

          <section style={{ marginBottom: '4rem' }}>
            <CurrencyClusters theme={theme} data={fxData} />
          </section>

          <section style={{ marginBottom: '4rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
             <MacroChart theme={theme} data={commerceData} />
             <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
               <LoreCard 
                 theme={theme} pair="Carry Trade" title="Diferencial de Juros (Selic vs Fed)"
                 lore="Tomar dinheiro emprestado no Japão (juro zero) e aplicar no Brasil (Selic alta). Quando a volatilidade sobe, esse trade se desfaz, causando saltos no câmbio."
               />
               <LoreCard 
                 theme={theme} pair="Petrodólar" title="O Fim do Acordo Saudita?"
                 lore="Mudanças na forma como o petróleo global é precificado podem impactar o fluxo mundial do dólar, afetando diretamente moedas emergentes de commodities como o BRL."
               />
             </div>
          </section>

          <NewsCardsSection theme={theme} itemsPerRow={3} />

        </main>
      </div>
    </div>
  );
}